<template>
  <div class="person-card-root">
    <v-card
      class="person-card pa-4"
      :class="{ 'person-card--novo': highlighted }"
      elevation="2"
    >
      <v-card-title class="d-flex align-center pa-0 person-card-header">
        <v-list-item class="px-0">
          <v-list-item-avatar class="avatar-wrapper mr-3">
            <img v-if="hasPhoto && photoUrl" :src="photoUrl" :alt="item.name" />
            <!-- Sem foto: iniciais, em vez da imagem padrão genérica. -->
            <span v-else class="avatar-initials">{{ initials }}</span>
          </v-list-item-avatar>

          <v-list-item-content class="flex-grow-1">
            <v-list-item-title class="text-h9 font-weight-medium mb-1">
              {{ item.name }}
            </v-list-item-title>
            <!-- Só o que existe: o cadastro rápido (SALVIA-945) não tem
                 nascimento nem gênero, e antes aparecia "Data inválida anos". -->
            <v-list-item-subtitle
              v-if="metaLine"
              class="text-body-2 grey--text text--darken-1"
            >
              {{ metaLine }}
            </v-list-item-subtitle>
            <v-list-item-subtitle
              v-if="isDoctor && item.specialty"
              class="text-body-2 primary--text mt-1"
            >
              {{ item.specialty }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-menu bottom left offset-y>
            <template v-slot:activator="{ on: menu, attrs }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on: tooltip }">
                  <v-btn
                    icon
                    small
                    class="menu-button"
                    data-tour="pp-card-menu"
                    v-bind="attrs"
                    v-on="{ ...menu, ...tooltip }"
                  >
                    <v-icon size="20">mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <span>Mais opções</span>
              </v-tooltip>
            </template>

            <v-list>
              <v-list-item
                v-if="userType === 'assistant' && isDoctor"
                @click="
                  $emit('open-dialog', {
                    type: 'pacientes-do-medico',
                    data: item,
                  })
                "
              >
                <v-list-item-title>Pacientes do Médico</v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if="userType === 'assistant' && cardType === 'paciente'"
                @click="
                  $emit('open-dialog', {
                    type: 'medicos-do-paciente',
                    data: item,
                  })
                "
              >
                <v-list-item-title>Médicos do Paciente</v-list-item-title>
              </v-list-item>

              <v-list-item @click="openDetailsDialog">
                <v-list-item-title>Ver detalhes</v-list-item-title>
              </v-list-item>

              <v-list-item @click="handleEditPerson(item.id)">
                <v-list-item-title>Editar</v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if="cardType === 'paciente'"
                @click="
                  $emit('open-dialog', {
                    type: 'dependentes-do-responsavel',
                    data: item,
                  })
                "
              >
                <v-list-item-title>Ver dependentes</v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if="cardType === 'paciente'"
                @click="$emit('add-dependent', item)"
              >
                <v-list-item-title>Adicionar dependente</v-list-item-title>
              </v-list-item>

              <!-- SALVIA-955: só some da lista DESTE médico; nada é apagado. -->
              <template v-if="canHide">
                <v-divider class="my-1" />
                <v-list-item @click="$emit('toggle-hidden', item)">
                  <v-list-item-title>
                    {{ item.hidden ? "Reexibir na minha lista" : "Ocultar da minha lista" }}
                  </v-list-item-title>
                </v-list-item>
              </template>
            </v-list>
          </v-menu>
        </v-list-item>
      </v-card-title>

      <v-card-text class="pa-0">
        <!-- Dados em linhas compactas; o que não existe não aparece (sem "N/A"). -->
        <div v-if="nextApptLabel" class="person-next">
          <v-icon small class="mr-1">mdi-calendar-clock</v-icon>
          Próxima consulta: <strong class="ml-1">{{ nextApptLabel }}</strong>
        </div>
        <!-- Os três dados de sempre, compactos lado a lado; o que falta aparece
             como "—" (antes, "N/A"), com "Não informado" ao passar o mouse. -->
        <div class="person-info">
          <div v-for="f in infoFields" :key="f.label" class="person-info__item">
            <span class="person-info__label">{{ f.label }}</span>
            <span
              class="person-info__value"
              :class="{ 'person-info__value--empty': !f.value }"
              :title="f.value ? null : 'Não informado'"
            >
              {{ f.value || "—" }}
            </span>
          </div>
        </div>
        <div v-if="phoneLabel" class="person-detail mt-2">
          <v-icon x-small class="mr-1">mdi-phone-outline</v-icon>{{ phoneLabel }}
        </div>
      </v-card-text>

      <!-- Empurra os botões para o rodapé: cards da mesma linha ficam alinhados. -->
      <v-spacer class="person-card-fill"></v-spacer>
      <v-divider v-if="showActions" class="my-3"></v-divider>

      <v-card-actions class="pa-0" v-if="showActions">
        <div class="button-container" data-tour="pp-card-actions">
          <template v-if="showBothButtons">
            <!--
              salvia-778 — ata de 13/08: iniciar consulta sem ter que entrar no
              cadastro do paciente antes.

              Aqui o paciente JA e conhecido, entao nao ha o que perguntar: um
              clique leva ao prontuario com o atendimento aberto. Mesmo destino
              do botao no detalhe do compromisso (salvia-837); muda o ponto de
              partida.

              Vive dentro de `showBothButtons`, que ja significa "medico olhando
              card de paciente" — assistente nao ve.
            -->
            <v-btn
              class="action-button attend-button"
              rounded
              depressed
              color="error"
              @click="iniciarAtendimento"
            >
              Atender
            </v-btn>

            <v-btn
              class="action-button primary-button"
              rounded
              depressed
              color="primary"
              @click="navigateToProfile"
            >
              Acessar
            </v-btn>

            <v-btn
              class="action-button secondary-button"
              rounded
              depressed
              color="secondary"
              @click="scheduleAppointment"
            >
              Agendar
            </v-btn>
          </template>

          <v-btn
            v-else-if="showOnlySecondButton"
            class="action-button secondary-button full-width"
            rounded
            depressed
            color="secondary"
            @click="scheduleAppointment"
          >
            Agendar
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>

    <!-- COMPONENTE DIALOG -->
    <PersonDetailsDialog
      v-model="detailsDialogVisible"
      :personData="item"
      :cardType="cardType"
      @edit-person="handleEditPerson"
    />
  </div>
</template>

<script>
import PersonDetailsDialog from "@/features/patients/components/PersonDetailsDialog.vue";
import { getProfileImage } from "@/utils/getProfileImage";

export default {
  name: "PersonCard",

  components: { PersonDetailsDialog },

  props: {
    item: { type: Object, required: true, default: () => ({}) },
    cardType: {
      type: String,
      default: "paciente",
      validator: (value) => ["paciente", "médico", "medico"].includes(value),
    },
    userType: { type: String, required: true },
    /** Início do próximo agendamento (ISO), quando a página já o tem. */
    nextAppointment: { type: String, default: null },
    /** Recém-cadastrado: a borda pisca duas vezes (como no novo atendimento). */
    highlighted: { type: Boolean, default: false },
    /** SALVIA-955: o médico pode ocultar/reexibir este paciente da lista dele. */
    canHide: { type: Boolean, default: false },
  },

  data() {
    return {
      photoUrl: null,
      detailsDialogVisible: false,
      _avatarObjectUrl: null,
    };
  },

  watch: {
    item: {
      immediate: true,
      deep: false,
      handler: "loadAvatar",
    },
  },

  beforeDestroy() {
    this.revokeAvatarUrl();
  },

  computed: {
    hasPhoto() {
      return Boolean(
        this.item?.avatar || this.item?.imageName || this.item?.profile_image || this.item?.photo,
      );
    },
    initials() {
      const parts = String(this.item?.name || "").trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return "?";
      const first = parts[0][0];
      const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
      return (first + last).toUpperCase();
    },
    /** "38 anos" — só quando a idade é conhecida (o gênero está na faixa abaixo). */
    metaLine() {
      return Number.isFinite(Number(this.item?.age)) ? `${this.item.age} anos` : "";
    },
    /** Os três dados que o card sempre mostrou; null vira "Não informado". */
    infoFields() {
      return [
        { label: "Gênero", value: this.known(this.item?.gender) },
        { label: "Nascimento", value: this.known(this.item?.birthdate) },
        { label: "Tipo sanguíneo", value: this.known(this.item?.bloodType) },
      ];
    },
    phoneLabel() {
      return this.formatPhone(this.item?.primaryPhone?.number || this.item?.phones?.[0]?.number);
    },
    nextApptLabel() {
      if (!this.nextAppointment) return "";
      const d = new Date(this.nextAppointment);
      if (Number.isNaN(d.getTime())) return "";
      const date = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
      const time = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
      return `${date} · ${time}`;
    },
    isDoctor() {
      return this.cardType === "médico" || this.cardType === "medico";
    },

    showActions() {
      return (
        (this.userType === "doctor_solo" ||
          this.userType === "doctor_clinic" ||
          this.userType === "assistant") &&
        this.cardType === "paciente"
      );
    },

    showBothButtons() {
      return (
        (this.userType === "doctor_solo" ||
          this.userType === "doctor_clinic") &&
        this.cardType === "paciente"
      );
    },

    showOnlySecondButton() {
      return this.userType === "assistant" && this.cardType === "paciente";
    },
  },

  methods: {
    /** Valor de verdade, ou null para vazios e marcadores como "N/A". */
    known(v) {
      const t = String(v ?? "").trim();
      return t && !/^(n\/a|data inválida|null|undefined)$/i.test(t) ? t : null;
    },
    formatPhone(v) {
      const n = String(v || "").replace(/\D/g, "");
      if (n.length === 11) return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
      if (n.length === 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
      return n ? v : null;
    },
    async loadAvatar() {
      this.revokeAvatarUrl();

      const imageName =
        this.item?.avatar ||
        this.item?.imageName ||
        this.item?.profile_image ||
        this.item?.photo;

      const urlOrFallback = await getProfileImage(imageName);

      // se for blob URL, guarda para revogar depois
      if (
        typeof urlOrFallback === "string" &&
        urlOrFallback.startsWith("blob:")
      ) {
        this._avatarObjectUrl = urlOrFallback;
      }

      this.photoUrl = urlOrFallback;
    },

    revokeAvatarUrl() {
      if (this._avatarObjectUrl) {
        URL.revokeObjectURL(this._avatarObjectUrl);
        this._avatarObjectUrl = null;
      }
    },

    getRouteLink() {
      if (this.cardType === "paciente") {
        return `/patient-profile-dashboard/${this.item.id}`;
      } else if (this.isDoctor) {
        return `/doctor-profile-dashboard/${this.item.id}`;
      }
      return "#";
    },

    navigateToProfile() {
      const route = this.getRouteLink();
      if (route !== "#") {
        this.$router.push(route);
      }
    },

    /**
     * salvia-778: vai ao prontuario JA com o atendimento aberto.
     *
     * O `?atendimento=novo` e lido pelo dashboard (salvia-837), que abre o
     * modal na chegada e limpa o parametro em seguida — senao um F5 reabriria
     * o atendimento por cima do que o medico estivesse fazendo.
     */
    iniciarAtendimento() {
      if (this.cardType !== "paciente") return;

      this.$router.push({
        path: `/patient-profile-dashboard/${this.item.id}`,
        query: { atendimento: "novo" },
      });
    },

    openDetailsDialog() {
      this.detailsDialogVisible = true;
    },

    handleEditPerson(userId) {
      this.detailsDialogVisible = false;
      this.$emit("edit-person", {
        userId,
        targetType: this.cardType === "paciente" ? "patient" : "doctor",
      });
    },

    scheduleAppointment() {
      const primaryPhone = this.getPatientPhone();

      this.$emit("schedule-appointment", {
        id: this.item.id,
        name: this.item.name,
        email: this.item.email,
        phone: primaryPhone,
      });
    },

    getPatientPhone() {
      if (!this.item.phones || this.item.phones.length === 0) {
        return null;
      }

      const primaryPhone = this.item.phones.find(
        (phone) => phone.is_primary === 1 || phone.is_primary === true,
      );

      return primaryPhone ? primaryPhone.number : this.item.phones[0].number;
    },
  },
};
</script>

<style scoped>
/* Mesma altura na linha da grade, botões sempre no rodapé. */
.person-card-root {
  display: flex;
  flex-direction: column; /* o v-dialog de detalhes fica abaixo, sem ocupar coluna */
  width: 100%;
}
.person-card-root > .person-card {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
}
.person-card-fill {
  flex: 1 1 auto;
}

.person-card {
  border-radius: 16px !important;
  border: 1px solid var(--ds-blue-100) !important;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.3s ease;
  background: var(--ds-blue-50) !important; /* corpo do card na cor do profile-hero */
  box-shadow: 0 2px 8px rgba(31, 77, 63, 0.04) !important;
  overflow: hidden; /* mantém os cantos arredondados sobre o cabeçalho verde */
}

/* Cabeçalho verde (mesmo verde dos botões, no estilo da página de clínicas) */
.person-card-header {
  background: var(--ds-gradient-header);
  margin: -16px -16px 12px -16px; /* sangra sobre o pa-4 do card */
  min-height: 0;
  padding: 12px 16px !important;
}
/* especificidade extra (.person-card .person-card-header) pra vencer a regra
   genérica .person-card :deep(.v-list-item__title) que vem depois e usa cor escura */
.person-card .person-card-header :deep(.v-list-item__title) {
  color: #fff !important;
}
.person-card .person-card-header :deep(.v-list-item__subtitle) {
  color: rgba(255, 255, 255, 0.85) !important;
}
.person-card-header :deep(.v-icon) {
  color: #fff !important;
}
.person-card-header .menu-button:hover {
  background-color: rgba(255, 255, 255, 0.18) !important;
}

.person-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(31, 77, 63, 0.16) !important;
  border-color: var(--ds-blue-200) !important;
}

.avatar-wrapper {
  width: 52px !important;
  height: 52px !important;
  min-width: 52px !important;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid var(--ds-blue-100);
  transition: all 0.3s ease;
}

.person-card:hover .avatar-wrapper {
  border-color: var(--ds-primary);
  transform: scale(1.05);
}

.avatar-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Nome e idade do paciente */
.person-card :deep(.v-list-item__title) {
  font-size: 14px !important;
  font-weight: 700 !important;
  color: var(--ds-text-strong) !important;
  letter-spacing: -0.2px;
}

.person-card :deep(.v-list-item__subtitle) {
  font-size: 11px !important;
  color: var(--ds-text-soft) !important;
}

.menu-button {
  color: var(--ds-text-muted) !important;
  transition: all 0.2s ease;
}

.menu-button:hover {
  color: var(--ds-primary) !important;
  background-color: var(--ds-blue-100) !important;
}

/* ═══ Dados do card (linhas compactas) ═══ */
.person-next {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  background: var(--ds-primary-surface);
  border: 1px solid var(--ds-primary-border);
  color: var(--ds-primary-deep);
  font-size: 12px;
}
.person-next :deep(.v-icon) {
  color: var(--ds-primary) !important;
}
/* Gênero · Nascimento · Tipo sanguíneo: três colunas compactas. */
.person-info {
  display: grid;
  grid-template-columns: repeat(3, auto);
  justify-content: space-between; /* cada coluna na largura do conteúdo */
  gap: 8px;
}
.person-info__item {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.person-info__label {
  white-space: nowrap;
  font-size: 10px;
  font-weight: 500;
  color: var(--ds-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.3;
}
.person-info__value {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-text-strong);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.person-info__value--empty {
  font-weight: 500;
  color: var(--ds-text-muted);
}
.person-detail {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: var(--ds-text-600);
  white-space: nowrap;
}
.person-detail :deep(.v-icon) {
  color: var(--ds-primary) !important;
}


/* Sem foto: iniciais sobre o verde do cabeçalho. */
.avatar-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* Recém-cadastrado: a borda pisca duas vezes (como no novo atendimento). */
.person-card--novo {
  animation: person-novo-pulso 0.8s ease-in-out 2;
}
@keyframes person-novo-pulso {
  0%,
  100% {
    box-shadow: 0 2px 8px rgba(31, 77, 63, 0.04);
  }
  50% {
    box-shadow: 0 0 0 4px var(--ds-primary), 0 8px 20px rgba(31, 77, 63, 0.2);
  }
}

.button-container {
  display: flex;
  gap: 8px;
  width: 100%;
}

.action-button {
  height: 40px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.3px;
  flex: 1;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.primary-button {
  box-shadow: 0 2px 6px rgba(31, 77, 63, 0.18) !important;
}

.primary-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(31, 77, 63, 0.28) !important;
}

.secondary-button:hover {
  transform: translateY(-1px);
}

.full-width {
  width: 100%;
}

/* Menu dropdown items */
.person-card + * :deep(.v-list-item__title),
:deep(.v-menu__content .v-list-item__title) {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: var(--ds-text-700) !important;
}

@media (max-width: 600px) {
  .person-card {
    border-radius: 12px !important;
  }

  .avatar-wrapper {
    width: 50px !important;
    height: 50px !important;
  }

  .action-button {
    height: 36px !important;
    font-size: 11px !important;
  }
}
</style>